import { ROUTES } from './routes';

/**
 * What a USER is shown when something fails, and what they can do about it.
 *
 * Why this exists: `errorMsg` was rendered in exactly one place — the `[dev]` diagnostic block in
 * AppErrorBanner, behind `NODE_ENV !== 'production'`. So every specific message the editor wrote
 * ("Every slice is hidden — show at least one to chop.", "This slice is read-only…", the upload
 * limit) was invisible to real users, who always saw "Uh oh! Something went wrong!".
 *
 * The fix is NOT to show `errorMsg` directly: it also carries raw server text (`abort()` strings,
 * `Server error (500)`), which leaks internals and reads like a stack trace. Instead a message is
 * shown only when it is RECOGNISED here. Anything else keeps the generic line, so nothing
 * unreviewed can ever reach a user.
 */
export type ErrorHint = {
  /** One sentence, addressed to the user, saying what happened and what to do. */
  text: string;
  /** Optional one-click way out. `onClick` is supplied by the caller (e.g. open the auth modal). */
  action?: ErrorAction;
  /** A second way out, shown after the first. */
  secondaryAction?: ErrorAction;
};

/**
 * A link (`href`), or a button whose handler the caller supplies for its `kind`. `newTab` opens the
 * link in a new tab, for a way out that must not take the user's open work with it.
 */
export type ErrorAction = { label: string; href?: string; newTab?: boolean; kind?: 'signIn' | 'retryLayers' | 'skipLayers' };

/**
 * `CHOP_UNCONFIRMED:<projectId>`: the chop was sent, so the project exists, but the editor lost
 * track of it — every status check failed, or the job outran the wait.
 */
export const CHOP_UNCONFIRMED = 'CHOP_UNCONFIRMED';

/** The chop ran and the WORKER failed it. `CHOP_FAILED:too_large:<megapixels>` when the image was too big. */
export const CHOP_FAILED = 'CHOP_FAILED';

/**
 * The editor's error for a failed job. The worker's own words are not shown (raw text, see above),
 * except to recognise the one failure the user can fix: an image over its megapixel limit. That
 * wording lives in worker/src/processors/chop.js ("… exceeds the 40MP limit.") — keep them in step.
 */
export function chopFailedError(workerMessage?: string | null): string {
  const limit = /exceeds the (\d+(?:\.\d+)?)MP limit/.exec(workerMessage ?? '');
  return limit ? `${CHOP_FAILED}:too_large:${limit[1]}` : CHOP_FAILED;
}

/** Sentinels thrown by `chopService`, mapped to what the user should read. */
const SENTINELS: Record<string, ErrorHint> = {
  // 403 from ChopChopController::rechopExisting — the project has an owner and we are not it.
  // Overwhelmingly this is a session that ended mid-edit, so the way out is to sign back in.
  // Said without blame and without implying the work is gone: it is still on screen.
  NOT_PROJECT_OWNER: {
    text: 'You’re signed out, so this project can’t be saved from here. Sign back in and chop again — your work is still open.',
    action: { label: 'Sign in', kind: 'signIn' }
  },
  // The chop worked, but saving its layer stack failed even after retrying a blip (EditorPage).
  // It used to be swallowed: the redirect went ahead and the layers were simply gone. The editor
  // now stays, layers still open, so the one thing to say is that nothing is lost YET.
  LAYERS_NOT_SAVED: {
    text: 'Your slices are saved, but your layers didn’t save. They’re still open here.',
    action: { label: 'Try again', kind: 'retryLayers' },
    secondaryAction: { label: 'Continue without layers', kind: 'skipLayers' }
  },
  // The chop ran and failed. It read "Something went wrong", whose one link reloads the editor and
  // throws away the work — the one thing that survived.
  [CHOP_FAILED]: { text: 'The chop failed on our end. Your work is still open here, so you can try again.' },
  DRAFT_LIMIT_REACHED: {
    text: 'You’ve reached your draft limit. Delete a draft in My Slices to save another.',
    action: { label: 'My Slices', href: '/my-slices' }
  },
  // 429 from /chop-chop (chopService): the per-minute chop limit. Nothing failed and the work is
  // still open, so the generic banner and its reload link were the wrong answer.
  RATE_LIMITED: { text: 'You’re chopping faster than we allow. Wait a minute, then chop again. Your work is still open here.' },
  FILE_TOO_LARGE: { text: 'That image is larger than your plan allows.', action: { label: 'See Pro', href: ROUTES.PRICING } },
  UNSUPPORTED_MEDIA_TYPE: { text: 'That file isn’t an image we can slice. Try a JPG or PNG.' },
  // 503 from BillingController when Stripe keys or price IDs are absent. Before this the SDK threw
  // inside Cashier and the raw "api_key cannot be the empty string" reached the pricing page.
  //
  // Worded for both of its homes — the pricing page's checkout and the account panel's billing
  // portal. It used to open with "Checkout isn't available here", which read as a non-sequitur
  // under a Manage subscription button.
  BILLING_NOT_CONFIGURED: {
    text: 'This environment has no payment configuration, so billing can’t be opened here.'
  },
  // 404 from BillingController::portal — Stripe is reachable, this account simply has no customer
  // record. Deliberately does NOT say "subscribe first" like the server's own message: the only
  // way to see this is to be an admin acting as Pro, and the card above it already says Pro.
  // Telling that person to subscribe is the contradiction the card was rebuilt to avoid.
  NO_BILLING_ACCOUNT: {
    text: 'There’s no Stripe billing record for this account, so there’s nothing to manage yet.'
  }
};

/** `feature` slug → what the user was actually trying to do, for the Pro gate. */
const PRO_FEATURES: Record<string, string> = {
  reedit_projects: 'Re-editing a saved project',
  autosave_drafts: 'Saving drafts',
  layers: 'Layers'
};

/**
 * Messages the editor itself writes in plain English. They were always meant for the user; they
 * were simply never rendered in production. Listed explicitly so adding one is a deliberate act.
 */
const CLIENT_MESSAGES: ReadonlySet<string> = new Set([
  'No image loaded.',
  'No file selected.',
  'Every slice is hidden — show at least one to chop.',
  'This slice is read-only. Clone it to make your own editable copy.'
]);

/**
 * The user-facing hint for a raw error message, or `null` to keep the generic banner.
 *
 * `uploadLimitMessage` is passed in because it is built from the signed-in user's own limit.
 */
export function hintForError(errorMsg: string | null, uploadLimitMessage?: string): ErrorHint | null {
  if (!errorMsg) return null;

  if (errorMsg.startsWith('FEATURE_REQUIRES_PRO')) {
    const feature = errorMsg.split(':')[1] || '';
    const what = PRO_FEATURES[feature] || 'That';
    return { text: `${what} is a Pro feature.`, action: { label: 'See Pro', href: ROUTES.PRICING } };
  }

  // Lost track of a chop that WAS sent: an outage or the per-minute limit outlasting the retries,
  // a re-chop from another tab replacing the status token, or a job slower than the wait. It used
  // to read "Something went wrong", whose one link reloads the editor — untrue, as the project
  // exists, and it threw the work away. The project page says how the chop went; a new tab keeps
  // the work here in case it didn't.
  if (errorMsg.startsWith(`${CHOP_UNCONFIRMED}:`)) {
    const projectId = errorMsg.slice(CHOP_UNCONFIRMED.length + 1);
    return {
      text: 'Your chop was sent, but we lost track of it. Your work is still open here.',
      action: { label: 'Open your project', href: `/s/${encodeURIComponent(projectId)}`, newTab: true }
    };
  }

  if (errorMsg.startsWith(`${CHOP_FAILED}:too_large:`)) {
    const megapixels = errorMsg.slice(`${CHOP_FAILED}:too_large:`.length);
    return { text: `That image is too big to slice: the limit is ${megapixels} megapixels. Try a smaller one — your work is still open here.` };
  }

  if (SENTINELS[errorMsg]) return SENTINELS[errorMsg];
  if (uploadLimitMessage && errorMsg === uploadLimitMessage) return { text: errorMsg };
  if (CLIENT_MESSAGES.has(errorMsg)) return { text: errorMsg };

  // Unrecognised — a raw server string or a bug. The generic banner is the honest answer.
  return null;
}
